/**
 * useChartLayout Hook
 * React hook for managing multi-chart layouts
 */

import { useState, useEffect, useCallback } from 'react';
import { LayoutType, LayoutConfig, ChartPanelConfig } from '../types/chartLayout';
import { ChartLayoutManager, getLayoutManager } from '../services/chartLayoutManager';

export function useChartLayout() {
  const [layoutManager] = useState<ChartLayoutManager>(() => getLayoutManager());
  const [layout, setLayout] = useState<LayoutConfig>(() => layoutManager.getLayout());

  // Subscribe to layout changes
  useEffect(() => {
    const unsubscribe = layoutManager.subscribe('layout-changed', (newLayout) => {
      setLayout(newLayout);
    });

    const unsubscribePanel = layoutManager.subscribe('panel-updated', (newLayout) => {
      setLayout(newLayout);
    });

    const unsubscribeSync = layoutManager.subscribe('sync-changed', (newLayout) => {
      setLayout(newLayout);
    });

    return () => {
      unsubscribe();
      unsubscribePanel();
      unsubscribeSync();
    };
  }, [layoutManager]);

  // Change layout type
  const changeLayout = useCallback((layoutType: LayoutType, preserveSymbols: boolean = true, currentSymbol?: string, currentTimeframe?: string) => {
    layoutManager.changeLayout(layoutType, preserveSymbols, currentSymbol, currentTimeframe);
  }, [layoutManager]);

  // Update panel symbol
  const updatePanelSymbol = useCallback((panelId: string, symbol: string) => {
    layoutManager.updatePanelSymbol(panelId, symbol);
  }, [layoutManager]);

  // Update panel timeframe
  const updatePanelTimeframe = useCallback((panelId: string, timeframe: string) => {
    layoutManager.updatePanelTimeframe(panelId, timeframe);
  }, [layoutManager]);

  // Update panel configuration
  const updatePanel = useCallback((panelId: string, updates: Partial<ChartPanelConfig>) => {
    layoutManager.updatePanel(panelId, updates);
  }, [layoutManager]);

  // Update all panels with the same configuration (batch update)
  const updateAllPanels = useCallback((updates: Partial<ChartPanelConfig>) => {
    layoutManager.updateAllPanels(updates);
  }, [layoutManager]);

  // Toggle sync options
  const toggleSync = useCallback((syncType: 'crosshair' | 'timeframe' | 'symbol') => {
    layoutManager.toggleSync(syncType);
  }, [layoutManager]);

  // Get specific panel
  const getPanel = useCallback((panelId: string) => {
    return layoutManager.getPanel(panelId);
  }, [layoutManager]);

  // Export layout
  const exportLayout = useCallback(() => {
    return layoutManager.exportLayout();
  }, [layoutManager]);

  // Import layout
  const importLayout = useCallback((layoutJson: string) => {
    layoutManager.importLayout(layoutJson);
  }, [layoutManager]);

  // Reset layout
  const resetLayout = useCallback(() => {
    layoutManager.reset();
  }, [layoutManager]);

  return {
    layout,
    panels: layout.panels,
    layoutType: layout.type,
    syncCrosshair: layout.syncCrosshair || false,
    syncTimeframe: layout.syncTimeframe || false,
    syncSymbol: layout.syncSymbol || false,
    changeLayout,
    updatePanelSymbol,
    updatePanelTimeframe,
    updatePanel,
    updateAllPanels,
    toggleSync,
    getPanel,
    exportLayout,
    importLayout,
    resetLayout
  };
}
